import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthResponse, LoginDto, UserDto } from './auth.model';

import { GlobalConstants } from '../../config/GlobalConstants';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http   = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly base   = environment.apiUrl + environment.apiBase;

  private readonly _user   = signal<UserDto | null>(this.readUser());
  readonly user            = this._user.asReadonly();
  readonly isAuthenticated = computed(() => !!this._user() && !!this.token());

  /** True only for ADMIN role — the only role that may use this admin panel. */
  readonly isAdmin = computed(() => this._user()?.role === 'ADMIN');

  /** Alias — guards use this to confirm admin shell access. */
  readonly canEnterAdmin = computed(() => this.isAdmin());

  login(payload: LoginDto): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.base}/auth/login`, payload)
      .pipe(tap(res => this.persist(res)));
  }

  getMe(): Observable<UserDto> {
    return this.http
      .get<UserDto>(`${this.base}/auth/me`)
      .pipe(tap(u => this.persistUser(u)));
  }

  logout(): void {
    const refresh = this.refreshToken();
    if (refresh) {
      this.http
        .post(`${this.base}/auth/logout`, { refreshToken: refresh })
        .subscribe({ next: () => {}, error: () => {} });
    }
    this.clear();
    this.router.navigateByUrl('/login');
  }

  token(): string | null {
    try { return localStorage.getItem(GlobalConstants.TOKEN_KEY); } catch { return null; }
  }

  refreshToken(): string | null {
    try { return localStorage.getItem(GlobalConstants.REFRESH_KEY); } catch { return null; }
  }

  private persistUser(u: UserDto | null): void {
    try { if (u) localStorage.setItem(GlobalConstants.USER_KEY, JSON.stringify(u)); } catch { /* blocked */ }
    this._user.set(u);
  }

  private persist(res: AuthResponse): void {
    try {
      if (res.accessToken)  localStorage.setItem(GlobalConstants.TOKEN_KEY, res.accessToken);
      if (res.refreshToken) localStorage.setItem(GlobalConstants.REFRESH_KEY, res.refreshToken);
      if (res.user)         localStorage.setItem(GlobalConstants.USER_KEY, JSON.stringify(res.user));
    } catch { /* blocked */ }
    this._user.set(res.user ?? null);
  }

  private readUser(): UserDto | null {
    try {
      const raw = localStorage.getItem(GlobalConstants.USER_KEY);
      return raw ? (JSON.parse(raw) as UserDto) : null;
    } catch { return null; }
  }

  private clear(): void {
    try {
      localStorage.removeItem(GlobalConstants.TOKEN_KEY);
      localStorage.removeItem(GlobalConstants.REFRESH_KEY);
      localStorage.removeItem(GlobalConstants.USER_KEY);
    } catch { /* ignore */ }
    this._user.set(null);
  }
}
